import React, { useCallback } from 'react';
import SafeImage from '../SafeImage';
import VideoThumb from './VideoThumb/VideoThumb';
import SmallSpinner from '../SmallSpinner/SmallSpinner';

/**
 * Thumbnail for an image or video attachment, either a local `File` or an uploaded
 * attachment object with `*_url` fields. Renders nothing (and warns) for other types.
 *
 * @param {Object} props
 * @param {File|Object} props.file - Attachment to show; its `type` or `mimetype` selects image vs. video.
 * @param {number|string} [props.size] - Thumbnail height; also selects the `<size>_url` image variant,
 *   falling back to `220_url` then `full_url`.
 * @param {string} [props.className] - Extra class name(s), added after `Thumbnail`.
 * @param {(attachment: File|Object) => void} [props.onClick] - Called with the attachment when clicked;
 *   propagation and default behavior are stopped.
 * @returns {JSX.Element|null}
 */
export default function Thumbnail(props) {
    const { file: attachment, size: sizeProp, className: classNameProp, onClick } = props,
        mimetype = attachment.type || attachment.mimetype,
        size = sizeProp || '',
        className = ['Thumbnail', classNameProp, onClick ? 'clickable' : ''].join(' ');

    const handleAttachmentClicked = useCallback((e) => {
        if (onClick) {
            e.stopPropagation();
            e.preventDefault();
            onClick(attachment);
        }
    }, [onClick, attachment]);

    if (mimetype.match(/video/)) {
        if (attachment instanceof File) {
            return <VideoThumb className={className} file={attachment} style={{ height: sizeProp }} onClick={handleAttachmentClicked} />;
        }
        return <VideoThumb className={className} src={attachment['220_url']} mediaSrc={attachment['media_url']} style={{ height: sizeProp }} onClick={handleAttachmentClicked} />;
    } else if (mimetype.match(/image/)) {
        let src;
        if (attachment instanceof File) {
            src = URL.createObjectURL(attachment);
        } else {
            src = attachment[(size.toString() || '') + '_url'] || attachment['220_url'] || attachment['full_url'];
        }
        if (src && src != 'None') {
            return <SafeImage className={className} src={src} altSrc="/img/pixel.png" height={sizeProp} onClick={handleAttachmentClicked} />;
        }
        return <SmallSpinner visible size={sizeProp} />;
    }

    console.warn('Unknown attachment type?', attachment);
    return null;
}
